import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLocalProfile } from "../local/useLocalProfile";
import { useMpRoom } from "../multiplayer/useMpRoom";
import { getBattlePlayerId } from "../multiplayer/battleIdentity";
import { AvatarPreview } from "../components/AvatarEditor";
import { Fireworks } from "../components/Fireworks";
import type { MpPlayer } from "../multiplayer/types";

/**
 * Composite ranking: accuracy (correct/total) → score → time.
 * DNFs sink to the bottom by score.
 */
function rankPlayers(players: MpPlayer[]): MpPlayer[] {
  return [...players].sort((a, b) => {
    const aDone = a.finished;
    const bDone = b.finished;
    if (aDone !== bDone) return aDone ? -1 : 1;
    if (!aDone) return (b.finishScore ?? 0) - (a.finishScore ?? 0);
    const aAcc = (a.finishCorrect ?? 0) / Math.max(1, a.finishTotal ?? 1);
    const bAcc = (b.finishCorrect ?? 0) / Math.max(1, b.finishTotal ?? 1);
    if (aAcc !== bAcc) return bAcc - aAcc;
    if ((b.finishScore ?? 0) !== (a.finishScore ?? 0))
      return (b.finishScore ?? 0) - (a.finishScore ?? 0);
    return (a.finishTimeMs ?? 1e9) - (b.finishTimeMs ?? 1e9);
  });
}

export function RaceResults() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { profile } = useLocalProfile();
  const code = params.get("code");

  if (!profile) {
    nav("/", { replace: true });
    return null;
  }
  const me = {
    id: getBattlePlayerId(profile.id, code),
    displayName: profile.displayName,
    avatarConfig: profile.avatarConfig,
  };

  const joinOpts = useMemo(
    () => (code ? { code, asHost: false, me } : null),
    [code, me.id],
  );
  const { state } = useMpRoom(joinOpts);

  if (!state) return <div className="full-center">Loading…</div>;
  const ranked = rankPlayers(state.players);
  const myRank = ranked.findIndex((p) => p.id === me.id) + 1;
  const winner = ranked[0];
  const youWon = winner?.id === me.id;

  return (
    <div className="results-overlay">
      {youWon && <Fireworks />}
      <div className={`results-card2 ${youWon ? "flawless" : ""}`}>
        <div className="results-trophy">{youWon ? "🏆" : "🏁"}</div>
        <div className="results-banner">
          <span>{youWon ? "You won!" : `Finished #${myRank}`}</span>
        </div>

        {/* Top-3 podium */}
        <div className="podium">
          {[2, 1, 3].map((place) => {
            const p = ranked[place - 1];
            if (!p) return <div key={place} className={`podium-step empty step-${place}`} />;
            return (
              <div key={place} className={`podium-step step-${place}`}>
                <div className="podium-medal">
                  {place === 1 ? "🥇" : place === 2 ? "🥈" : "🥉"}
                </div>
                <div className="podium-avatar">
                  <AvatarPreview cfg={p.avatarConfig} />
                </div>
                <div className="podium-name">{p.displayName}</div>
                <div className="podium-stats t-muted">
                  {p.finished
                    ? `${(p.finishTimeMs! / 1000).toFixed(1)}s · ${p.finishCorrect ?? 0}/${p.finishTotal ?? 0}`
                    : "DNF"}
                </div>
              </div>
            );
          })}
        </div>

        {/* Full ranking */}
        <div className="divider" />
        {ranked.map((p, i) => (
          <div
            key={p.id}
            className="row-between"
            style={{
              padding: "8px 4px",
              fontWeight: p.id === me.id ? 800 : 600,
              color: p.id === me.id ? "var(--c-purple-2)" : "inherit",
            }}
          >
            <span style={{ minWidth: 28 }}>#{i + 1}</span>
            <span style={{ flex: 1 }}>{p.displayName}{p.id === me.id ? " (you)" : ""}</span>
            <span className="t-muted">
              {p.finished ? `${p.finishCorrect ?? 0}/${p.finishTotal ?? 0}` : "—"}
            </span>
            <strong style={{ marginLeft: 12 }}>
              {p.finished ? `${(p.finishTimeMs! / 1000).toFixed(1)}s` : "DNF"}
            </strong>
          </div>
        ))}

        <div className="results-actions2">
          <button className="btn btn-orange btn-big btn-block" onClick={() => nav("/battle")}>
            🏁 Race again
          </button>
          <button className="btn btn-primary btn-big btn-block" onClick={() => nav("/")}>
            → Menu
          </button>
        </div>
      </div>
    </div>
  );
}
